























import { allSettled } from '@/common/allSettled'
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import util from '@/common/util'

enum BusinessDynamicTypeEnum {
  UNKNOW = '',
  /** 定保 */
  SOLICIT = 'solicit',
  /** 续保 */
  INS = 'ins',
  /** 养修预约 */
  SERVICE_BOOKING = 'service_booking',
  /** 卡券 */
  COUPONS = 'coupons',
  /** 客户关怀 */
  CUSTOM_CARE = 'custom_care',
  /** 活动 */
  ACTIVITY = 'activity',
}

type RenderDynamicItem = {
  isUrgent: boolean
  text: string
}

enum TipsTypeEnum {
  // 定保提醒
  /** 首次保养 */
  FIRST_MAINTAIN = 'first_maintain',
  /** 定期保养 */
  FIXED_MAINTAIN = 'fixed_maintain',
  /** 维修到期 */
  REPAIR_EXPIRED = 'repair_expired',

  // 续保提醒
  /** 交强险到期 */
  FORCE_INS_EXPIRED = 'force_ins_expired',
  /** 商业险到期 */
  BUSINESS_INS_EXPIRED = 'business_ins_expired',

  // 养修预约提醒类型
  /** 预约 */
  BOOKING = 'booking',

  // 卡券提醒类型
  /** 即将到期 */
  NEARLY_EXPIRED = 'nearly_expired',

  // 客户关怀提醒类型
  /** 生日关怀 */
  CUSTOM_BIRTHDAY = 'custom_birthday',
  /** 购车纪念日 */
  CAR_PURCHASE_ANNIVERSARY = 'car_purchase_anniversary',

  // 活动提醒类型
  /** 集客报名 */
  ACTIVITY_ENROLLMENT = 'activity_enrollment',
}

const SolicitTipsTextMap = {
  [TipsTypeEnum.FIRST_MAINTAIN]: '首保',
  [TipsTypeEnum.FIXED_MAINTAIN]: '定保',
  [TipsTypeEnum.REPAIR_EXPIRED]: '保修到期'
}

type BaseBusinessDynamicTipsData<BizType, Type, Content> = {
  biz_type: BizType
  dynamic_list: {
    type: Type
    color: 'green' | 'red'
    content: Content
  }[]
}

/** 定保提醒 */
type SolicitTipsData = BaseBusinessDynamicTipsData<
  BusinessDynamicTypeEnum.SOLICIT,
  TipsTypeEnum.FIRST_MAINTAIN | TipsTypeEnum.FIXED_MAINTAIN | TipsTypeEnum.REPAIR_EXPIRED,
  { biz_date: string }
>

/** 续保提醒 */
type InsTipsData = BaseBusinessDynamicTipsData<
  BusinessDynamicTypeEnum.INS,
  TipsTypeEnum.FORCE_INS_EXPIRED | TipsTypeEnum.BUSINESS_INS_EXPIRED,
  { biz_date: string }
>

/** 养修预约 */
type ServiceBookingTipsData = BaseBusinessDynamicTipsData<
  BusinessDynamicTypeEnum.SERVICE_BOOKING,
  TipsTypeEnum.BOOKING,
  { appoint_time: string }
>

/** 卡券 */
type CouponsTipsData = BaseBusinessDynamicTipsData<
  BusinessDynamicTypeEnum.COUPONS,
  TipsTypeEnum.NEARLY_EXPIRED,
  { coupons_num: number, sticky_plan_num: number }
>

/** 客户关怀 */
type CustomCareTipsData = BaseBusinessDynamicTipsData<
  BusinessDynamicTypeEnum.CUSTOM_CARE,
  TipsTypeEnum.CUSTOM_BIRTHDAY | TipsTypeEnum.CAR_PURCHASE_ANNIVERSARY,
  { date: string }
>

/** 活动 */
type ActivityTipsData = BaseBusinessDynamicTipsData<
  BusinessDynamicTypeEnum.ACTIVITY,
  TipsTypeEnum.ACTIVITY_ENROLLMENT,
  { activity_names: string[] }
>

type IBusinessDynamicTipsData =
  | SolicitTipsData
  | InsTipsData
  | ServiceBookingTipsData
  | CouponsTipsData
  | CustomCareTipsData
  | ActivityTipsData

const Aftersales = namespace('aftersales')

@Component({})
export default class CarBusinessDynamic extends Vue {
  @Aftersales.State('userInfo') userInfo: any;
  @Prop(String) readonly plateNumber!: string;
  @Prop(String) readonly vin!: string;

  @Watch('userInfo', { deep: true })
  onUserInfoChange() {
    this.getCarBusinessDynamicsTips()
  }
  @Watch('plateNumber')
  onPlateNumberChange() {
    this.getCarBusinessDynamicsTips()
  }
  @Watch('vin')
  onVinChange() {
    this.getCarBusinessDynamicsTips()
  }

  mounted() {
    this.getCarBusinessDynamicsTips()
  }

  loading = false
  list: RenderDynamicItem[] = []
  async getCarBusinessDynamicsTips() {
    if ((!this.plateNumber && !this.vin) || !this.userInfo.mobile_number) return
    if (this.loading) return
    this.loading = true
    try {
      const params = {
        plate_number: this.plateNumber,
        vin: this.vin,
        mobile_number: this.userInfo.mobile_number
      }
      const types = [
        BusinessDynamicTypeEnum.SOLICIT,
        BusinessDynamicTypeEnum.INS,
        BusinessDynamicTypeEnum.SERVICE_BOOKING,
        BusinessDynamicTypeEnum.COUPONS,
        BusinessDynamicTypeEnum.CUSTOM_CARE,
        BusinessDynamicTypeEnum.ACTIVITY
      ]

      const ress = await allSettled(types.map(type => this.$rest.aftersales.getCarBusinessDynamicsTips({ ...params, biz_type: type })))
      this.list = []
      ress.forEach((res: any) => {
        if (res.status === 'rejected') return
        if (!res.value) return
        const { code, data } = res.value
        if (code) return
        this.handleCarBusinessDynamicsTipsData(data)
      })

      this.list = this.list.sort((a, b) => Number(b.isUrgent) - Number(a.isUrgent))
    } finally {
      this.loading = false
    }
  }

  handleCarBusinessDynamicsTipsData(data: IBusinessDynamicTipsData) {
    const push = (item: IBusinessDynamicTipsData['dynamic_list'][number], text: string) => {
      this.list.push({
        isUrgent: item.color === 'red',
        text
      })
    }
    switch (data.biz_type) {
      // 定保
      case BusinessDynamicTypeEnum.SOLICIT:
        data.dynamic_list.forEach(item => {
          const typeStr = SolicitTipsTextMap[item.type]
          push(item, `临近${typeStr}日期 ${typeStr}日期：${util.formatTime(item.content.biz_date, 'MM.dd')}`)
        })
        break
      // 续保
      case BusinessDynamicTypeEnum.INS:
        let _data: RenderDynamicItem | null = null
        data.dynamic_list.forEach(item => {
          // 优先展示商业险
          if (!_data || item.type === TipsTypeEnum.BUSINESS_INS_EXPIRED) {
            _data = {
              isUrgent: item.color === 'red',
              text: `临近续保期 续保日期：${util.formatTime(item.content.biz_date, 'MM.dd')}`
            }
          }
        })
        if (_data) this.list.push(_data)
        break
      // 养修预约
      case BusinessDynamicTypeEnum.SERVICE_BOOKING:
        data.dynamic_list.forEach(item => {
          const isToday = item.content.appoint_time === util.formatTime(new Date(), 'yyyy-MM-dd')
          push(item, isToday ? '已预约今日暂未到店' : `已预约 ${util.formatTime(item.content.appoint_time, 'M月d日 hh:mm')} 到店`)
        })
        break
      // 卡券
      case BusinessDynamicTypeEnum.COUPONS:
        data.dynamic_list.forEach(item => {
          push(item, `有 ${item.content.coupons_num} 张卡券（粘度套餐 ${item.content.sticky_plan_num} 张）即将到期`)
        })
        break
      // 客户关怀
      case BusinessDynamicTypeEnum.CUSTOM_CARE:
        data.dynamic_list.forEach(item => {
          let text = ''
          const dateStr = util.formatTime(item.content.date, 'MM.dd')
          if (util.formatTime(new Date(), 'MM.dd') !== dateStr) return
          if (item.type === TipsTypeEnum.CAR_PURCHASE_ANNIVERSARY) {
            // 购车第几年
            const _years = Number(util.formatTime(new Date(), 'yyyy')) - Number(util.formatTime(item.content.date, 'yyyy'))
            // 当天购车的不提示
            if (!_years) return
            text = `今日购车 ${_years} 周年，购车日：${dateStr}`
          } else if (item.type === TipsTypeEnum.CUSTOM_BIRTHDAY) {
            text = `今日客户生日，生日: ${dateStr}`
          }
          push(item, text)
        })
        break
      // 活动
      case BusinessDynamicTypeEnum.ACTIVITY:
        data.dynamic_list.forEach(item => {
          push(item, `已参加活动：${item.content.activity_names.join('、')}`)
        })
        break
    }
  }
}
